import {
  CREATE_COMMENT,
  DELETE_COMMENT,
  FETCH_COMMENT_BY_ID,
  FETCH_COMMENTS,
  FETCH_COMMENTS_BY_CATALOG,
  SEARCH_COMMENTS,
  SEARCH_HUB_CATALOGS,
  UPDATE_COMMENT,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubComment } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  getHubCatalogByIdKey,
  HUB_CATALOG_BY_ID_AND_VERSION_KEY,
  HUB_CATALOG_BY_ID_KEY,
  HUB_CATALOGS_KEY,
} from "./HubCatalogs";

export const HUB_COMMENTS_KEY = "hubCommentsKey";
export const HUB_COMMENT_BY_ID_KEY = "hubCommentByIdKey";
export const HUB_COMMENTS_BY_CATALOG_KEY = "hubCommentsByCatalogKey";

export const getHubCommentsKey = () => [HUB_COMMENTS_KEY] as const;

export const getHubCommentByIdKey = (commentId: number) =>
  [HUB_COMMENT_BY_ID_KEY, commentId] as const;

export const getHubCommentsByCatalogKey = (catalogId: number) =>
  [HUB_COMMENTS_BY_CATALOG_KEY, catalogId] as const;

export const useHubComments = (
  options?: Partial<
    UseQueryOptions<
      IHubComment[],
      AxiosError,
      IHubComment[],
      ReturnType<typeof getHubCommentsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCommentsKey(),
    queryFn: async () => {
      return mutationFetch<IHubComment[]>({
        url: FETCH_COMMENTS,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubComment = (
  commentId: number,
  options?: Partial<
    UseQueryOptions<
      IHubComment,
      AxiosError,
      IHubComment,
      ReturnType<typeof getHubCommentByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCommentByIdKey(commentId),
    queryFn: async () => {
      return mutationFetch<IHubComment>({
        url: FETCH_COMMENT_BY_ID.replace(":commentId", String(commentId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && commentId > 0,
  });
};

export const useHubCommentsByCatalog = (
  catalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubComment[],
      AxiosError,
      IHubComment[],
      ReturnType<typeof getHubCommentsByCatalogKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCommentsByCatalogKey(catalogId),
    queryFn: async () => {
      return mutationFetch<IHubComment[]>({
        url: FETCH_COMMENTS_BY_CATALOG.replace(":catalogId", String(catalogId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && catalogId > 0,
  });
};

const createHubComment = (body: IHubComment): Promise<IHubComment> => {
  return mutationFetch<IHubComment>({
    url: CREATE_COMMENT,
    method: "POST",
    body,
  });
};

const updateHubComment = (
  commentId: number,
  body: IHubComment,
): Promise<IHubComment> => {
  return mutationFetch<IHubComment>({
    url: UPDATE_COMMENT.replace(":commentId", String(commentId)),
    method: "PUT",
    body,
  });
};

const deleteHubComment = (commentId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_COMMENT.replace(":commentId", String(commentId)),
    method: "DELETE",
  });
};

export const useCreateHubComment = (
  options?: UseMutationOptions<IHubComment, AxiosError, IHubComment>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubComment,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_COMMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_COMMENTS] });
      if (data.commentId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCommentByIdKey(data.commentId),
        });
      }
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCommentsByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
        queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
    },
    ...options,
  });
};

export const useUpdateHubComment = (
  options?: UseMutationOptions<IHubComment, AxiosError, IHubComment>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IHubComment) => {
      const commentId = body.commentId;
      if (commentId == null) {
        throw new Error("commentId is required for update");
      }
      return updateHubComment(commentId, body);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_COMMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_COMMENTS] });
      if (data.commentId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCommentByIdKey(data.commentId),
        });
      }
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCommentsByCatalogKey(data.catalogId),
        });
        queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
        queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubComment = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubComment,
    onSuccess: (_, commentId) => {
      queryClient.invalidateQueries({ queryKey: [HUB_COMMENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_COMMENTS] });
      queryClient.invalidateQueries({
        queryKey: getHubCommentByIdKey(commentId),
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_COMMENTS_BY_CATALOG_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
    },
    ...options,
  });
};

export const useSearchHubComments = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubComment>, "url" | "payload">,
) => {
  return useSearchQuery<IHubComment>({
    url: SEARCH_COMMENTS,
    payload,
    ...options,
  });
};
